import { UPDATE_BUDGET } from 'graphql/vacancies/mutations';
import { useMutation } from 'hooks/sympl-mutation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import 'twin.macro';
import { redirectToStripeCheckout } from 'utils/paymentHelpers';
import { CheckoutReturnedError } from 'utils/checkoutErrors';
import BudgetSelector from './BudgetSelector';

import {
  Error,
  Subhead,
  Title2,
  Title3,
} from 'components/typography/Typography';
import useNavigationContext from 'hooks/context/nav-context';
import Checkbox from 'components/checkbox/Checkbox';
import { useQuery } from 'hooks/sympl-query';
import { GET_VACANCY_BUDGET } from 'graphql/vacancies/queries';
import Button from 'components/button/Button';
import { VacancyMetric } from 'types/vacancyTypes';
import RadioGroup from 'components/form/RadioGroup';
import Label from 'components/form/Label';
import InformationToolTip from 'components/tooltip/InformationToolTip';
import { campaignRequestErrorMessage } from 'utils/campaignRequestErrors';
import { useToastNotifications } from 'hooks/notificationHooks';
import { ToastTypes } from 'types/notificationTypes';
import { useCheckoutRecovery } from 'hooks/useCheckoutRecovery';

interface UpdateBudgetResponse {
  updatedBudget: {
    session_id?: string;
  };
}

export enum PaymentMethod {
  CREDIT_CARD = 'CREDIT_CARD',
  WIRE_TRANSFER = 'WIRE_TRANSFER',
}

interface UpdateBudgetPayload {
  vacancyId: number;
  input: {
    total_budget: number;
    use_balance?: boolean;
    payment_method?: PaymentMethod;
  };
}

const MIN_AD_BUDGET = 100;
const MAX_AD_BUDGET = 5000;

interface BudgetProps {
  title?: string;
  subtitle?: string | JSX.Element;
  cta: string;
  updateBudget?: boolean;
  onSubmit?: (
    budget: number,
    paymentMethod?: PaymentMethod,
    useBalance?: boolean
  ) => void | Promise<void>;
  closeModal?: () => void;
  onSubmittingChange?: (value: boolean) => void;
  isSubmitting?: boolean;
}

const Budget: React.FC<BudgetProps> = ({
  subtitle,
  title,
  cta,
  updateBudget = true,
  onSubmit,
  closeModal,
  onSubmittingChange,
  isSubmitting: submittingFromParent = false,
}) => {
  const { control, handleSubmit, reset: resetForm, watch, errors } = useForm();

  const [updateBudgetCall] = useMutation<
    UpdateBudgetResponse,
    UpdateBudgetPayload
  >(UPDATE_BUDGET);

  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const { addToast } = useToastNotifications();
  const recoverCheckout = useCheckoutRecovery();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const { activeVacancy, customer, refetchCustomer } = useNavigationContext();

  // TODO: const wireTransferEnabled = useFeatureFlagEnabled('wire-transfer');

  const {
    data,
    loading: budgetLoading,
    error: budgetError,
    refetch: refetchBudget,
  } = useQuery<
    {
      budget: { vacBudget: VacancyMetric['vacBudget'] };
    },
    { vacancyId: number }
  >(GET_VACANCY_BUDGET, {
    skip: !activeVacancy,
    fetchPolicy: 'network-only',
    variables: { vacancyId: activeVacancy ?? 0 },
  });

  const oldTotalBudget = useMemo(
    () => data?.budget?.vacBudget?.total_budget ?? 0,
    [data?.budget?.vacBudget?.total_budget]
  );
  const currentBudget = data?.budget?.vacBudget?.total_budget;
  const budgetReady =
    !budgetLoading &&
    !budgetError &&
    typeof currentBudget === 'number' &&
    Number.isFinite(currentBudget);
  const budgetUnavailable = updateBudget && (!activeVacancy || !budgetReady);

  const customerBalance = customer?.balance ?? 0;

  const useBalanceSelected = watch('use-balance');
  const remainingBudgetAfterBalance =
    watch('extra-budget') - (useBalanceSelected ? customerBalance : 0);

  const submitHandler = async (data: {
    'extra-budget': number;
    'use-balance'?: boolean;
    'payment-method'?: PaymentMethod;
  }) => {
    // The ref also stops submissions in the same render, including Enter presses.
    if (submitting.current || submittingFromParent || budgetUnavailable) return;
    submitting.current = true;
    setIsSubmitting(true);
    setSubmitError(undefined);

    const newTotalBudget = oldTotalBudget + data['extra-budget'];

    try {
      onSubmittingChange?.(true);
      if (!updateBudget) {
        await onSubmit?.(
          newTotalBudget,
          data['payment-method'],
          data['use-balance']
        );
        closeModal?.();
        return;
      }

      const response = await updateBudgetCall({
        variables: {
          vacancyId: activeVacancy!,
          input: {
            total_budget: newTotalBudget,
            use_balance: data['use-balance'] ?? false,
            payment_method: data['payment-method'],
          },
        },
      });

      const stripeId = response.data?.updatedBudget.session_id;
      if (stripeId) await redirectToStripeCheckout(stripeId);

      resetForm();
      closeModal?.();

      // The purchase is complete. Refresh failures must never invite another payment.
      // Defer each callback so synchronous failures are handled like rejected promises.
      const refreshes = await Promise.allSettled([
        Promise.resolve().then(() =>
          onSubmit?.(
            newTotalBudget,
            data['payment-method'],
            data['use-balance']
          )
        ),
        data['use-balance']
          ? Promise.resolve().then(() => refetchCustomer())
          : Promise.resolve(),
      ]);
      if (refreshes.some(({ status }) => status === 'rejected')) {
        addToast({
          title: 'Budget updated',
          description:
            'Your campaign budget was updated, but some information could not be refreshed. Refresh the page before making another budget change.',
          type: ToastTypes.WARNING,
        });
      }
    } catch (error) {
      if (error instanceof CheckoutReturnedError) {
        closeModal?.();
        resetForm();
        await recoverCheckout();
        return;
      }
      const message = campaignRequestErrorMessage(
        error,
        updateBudget
          ? 'Something went wrong while updating your campaign budget. Please try again.'
          : 'Something went wrong while publishing your campaign. Please try again.'
      );
      if (mounted.current) {
        setSubmitError(message);
      } else {
        // Metrics loading can replace this form before the purchase request settles.
        addToast({
          title: updateBudget ? 'Budget update failed' : 'Publishing failed',
          description: message,
          type: ToastTypes.ERROR,
        });
      }
    } finally {
      submitting.current = false;
      if (mounted.current) setIsSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  return (
    <form id="budget-form" tw="py-3" onSubmit={handleSubmit(submitHandler)}>
      <div>
        <Title2>{title}</Title2>
        <Subhead>{subtitle}</Subhead>
      </div>

      <div tw="my-6">
        <Controller
          id="extra-budget"
          name="extra-budget"
          control={control}
          defaultValue={400}
          rules={{
            min: MIN_AD_BUDGET,
            max: MAX_AD_BUDGET,
          }}
          render={({ value, onChange }) => (
            <BudgetSelector
              id="extra-budget"
              onChange={onChange}
              currentValue={value || MIN_AD_BUDGET}
              allowCustomValue={true}
              options={[400, 600, 800, 1000]}
              minValue={MIN_AD_BUDGET}
              maxValue={MAX_AD_BUDGET}
            />
          )}
        />
        {errors['extra-budget'] && (
          <Error>
            This budget is not allowed, please fill in at least €100 and maximum
            €5.000.
          </Error>
        )}
      </div>

      <div tw="mt-8">
        <Title3>Select payment method</Title3>
        <div tw="flex flex-row items-center justify-between px-4 py-2 bg-gray-50 rounded-md">
          <Controller
            id="use-balance"
            name="use-balance"
            control={control}
            defaultValue={customerBalance > 0}
            render={({ value, onChange }) => (
              <Checkbox
                disabled={customerBalance <= 0}
                label={`Use account balance (budget left: €${customerBalance.toLocaleString()})`}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <InformationToolTip
            name="budget_modal_account_balance_explanation"
            text="Make budget payments even easier by leveraging sympl's account balance feature. Head over to your account settings and top-up your balance via bank transfer."
          />
        </div>
        {remainingBudgetAfterBalance > 0 && (
          <div tw="mt-4 animate-fade-in-up">
            {useBalanceSelected && (
              <Label htmlFor="payment-method">Pay remainder via:</Label>
            )}
            <Controller
              id="payment-method"
              name="payment-method"
              control={control}
              render={({ onChange }) => (
                <RadioGroup
                  name="payment-method-radiogroup"
                  onChange={onChange}
                  items={[
                    {
                      id: PaymentMethod.CREDIT_CARD,
                      label: 'Credit Card (Mastercard, Visa...)',
                      value: PaymentMethod.CREDIT_CARD,
                    },
                    {
                      id: PaymentMethod.WIRE_TRANSFER,
                      label: 'Bank Transfer',
                      value: PaymentMethod.WIRE_TRANSFER,
                    },
                  ]}
                />
              )}
            />
          </div>
        )}
      </div>

      {oldTotalBudget > 0 && (
        <div tw="flex justify-end mt-4">
          <Subhead isBold>
            Your new total budget will be set to €{' '}
            {(oldTotalBudget + (watch('extra-budget') ?? 0)).toLocaleString()}.
          </Subhead>
        </div>
      )}

      {updateBudget && budgetLoading && (
        <p role="status">Loading your current campaign budget…</p>
      )}

      {budgetUnavailable && !budgetLoading && (
        <div role="alert">
          <Error>
            We could not load your current campaign budget. Please try again
            before adding budget.
          </Error>
          {!!activeVacancy && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                // Apollo exposes a retry failure through budgetError above.
                void refetchBudget().catch(() => undefined);
              }}
            >
              Retry loading budget
            </Button>
          )}
        </div>
      )}

      {submitError && (
        <div role="alert">
          <Error>{submitError}</Error>
        </div>
      )}

      <div tw="float-right pt-2 pb-4">
        <Button
          type="submit"
          loading={
            isSubmitting ||
            submittingFromParent ||
            (updateBudget && budgetLoading)
          }
          disabled={isSubmitting || submittingFromParent || budgetUnavailable}
        >
          {cta}
        </Button>
      </div>
    </form>
  );
};

export default Budget;
